// src/providers/ErrorBoundaryProvider.tsx
// ═══════════════════════════════════════════════════════════════════════════════
// ERROR BOUNDARY PROVIDER
// ═══════════════════════════════════════════════════════════════════════════════

import { Component, type ErrorInfo, type ReactNode } from "react";
import { AppError, isAppError } from "@/core/errors/classes";
import { globalErrorHandler } from "@/core/errors/handlers";
import { ErrorFallback } from "@/shared/components/ui/ErrorFallback/ErrorFallback";

interface Props {
  children: ReactNode;
  fallback?: ReactNode | ((error: AppError, reset: () => void) => ReactNode);
  onError?: (error: AppError, errorInfo: ErrorInfo) => void;
  scope?: string;
}

interface State {
  hasError: boolean;
  error: AppError | null;
}

export class ErrorBoundaryProvider extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    const appError = isAppError(error)
      ? error
      : new AppError(error.message, {
          cause: error,
          isOperational: false,
        });

    return { hasError: true, error: appError };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { onError, scope } = this.props;

    const appError = isAppError(error)
      ? error.withContext({
          component: scope ?? "ErrorBoundary",
          metadata: { componentStack: errorInfo.componentStack },
        })
      : new AppError(error.message, {
          cause: error,
          isOperational: false,
          context: {
            component: scope ?? "ErrorBoundary",
            timestamp: new Date(),
            metadata: { componentStack: errorInfo.componentStack },
          },
        });

    globalErrorHandler.handle(appError, {
      showToast: false,
      logError: true,
      reportError: true,
    });

    onError?.(appError, errorInfo);
  }

  resetError = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    const { children, fallback } = this.props;
    const { hasError, error } = this.state;

    if (hasError && error) {
      if (typeof fallback === "function") {
        return fallback(error, this.resetError);
      }

      if (fallback) {
        return fallback;
      }

      return <ErrorFallback error={error} onReset={this.resetError} />;
    }

    return children;
  }
}
